"use client";

import { useState } from "react";
import { Tag, ChevronRight, ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  CUSTOM_VALUE_CATEGORIES,
  type CustomValueCategory,
} from "@/lib/customValues";

interface CustomValuePickerProps {
  /** Called with the token (e.g. "{{contact.name}}") when a value is picked. */
  onSelect: (value: string) => void;
  /** Extra classes for the trigger button. */
  className?: string;
}

/**
 * GHL-style custom value picker: a tag-icon button that opens a popover with a
 * list of categories; selecting a category reveals its custom values, and
 * selecting a value inserts its token via `onSelect`.
 */
export function CustomValuePicker({
  onSelect,
  className,
}: CustomValuePickerProps) {
  const [open, setOpen] = useState(false);
  const [activeCategory, setActiveCategory] =
    useState<CustomValueCategory | null>(null);

  const reset = () => setActiveCategory(null);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) reset();
  };

  const handleSelect = (value: string) => {
    onSelect(value);
    setOpen(false);
    reset();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Insert custom value"
          className={cn("h-7 w-7 text-muted-foreground", className)}
        >
          <Tag className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        {activeCategory ? (
          <div>
            <button
              type="button"
              onClick={reset}
              className="flex w-full items-center gap-2 border-b px-3 py-2.5 text-sm font-medium"
            >
              <ArrowLeft className="h-4 w-4 text-blue-500" />
              {activeCategory.label}
            </button>
            <div className="max-h-72 overflow-y-auto py-1">
              {activeCategory.items.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => handleSelect(item.value)}
                  className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-accent"
                >
                  <span className="text-sm text-foreground">{item.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {item.value}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto py-1">
            {CUSTOM_VALUE_CATEGORIES.map((category) => (
              <button
                key={category.key}
                type="button"
                onClick={() => setActiveCategory(category)}
                className="flex w-full items-center justify-between px-3 py-2.5 text-sm text-foreground hover:bg-accent"
              >
                <span>{category.label}</span>
                <ChevronRight className="h-4 w-4 text-blue-500" />
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
